import { useEffect } from "react";
import { BRAND } from "@/lib/brand-config";

interface SEOProps {
  title: string;
  description?: string;
  path?: string;
  ogType?: string;
  ogTitle?: string;
  ogDescription?: string;
  ogUrl?: string;
  ogImage?: string;
  twitterCard?: string;
  jsonLd?: object | object[];
  /**
   * When true, emit `<meta name="robots" content="noindex, nofollow">` so the
   * page is kept out of search-engine indexes. Used by unlisted pages (e.g. the
   * screen-share sales walkthrough) that are reachable only by direct URL.
   */
  noindex?: boolean;
  /** Use `title` as the document title, without appending the site suffix. */
  exactTitle?: boolean;
}

const BASE_URL = BRAND.domainUrl;
const TITLE_SUFFIX = ` | ${BRAND.siteName}`;
const DEFAULT_FALLBACK_TITLE = `${BRAND.siteName}: EB-5 Investor Visa Advisory & Investment Platform`;
const DEFAULT_OG_IMAGE = BASE_URL + BRAND.socialImage;

function resolveOgImage(input?: string): string {
  if (!input) return DEFAULT_OG_IMAGE;
  if (/^https?:\/\//i.test(input)) return input;
  return BASE_URL + (input.startsWith("/") ? input : "/" + input);
}

export function useSEO(props: SEOProps) {
  useEffect(() => {
    const {
      title,
      description = "",
      path,
      ogType = "website",
      ogTitle,
      ogDescription,
      ogUrl,
      ogImage,
      twitterCard = "summary_large_image",
      jsonLd,
      noindex = false,
      exactTitle = false,
    } = props;

    const fullTitle = exactTitle || title.endsWith(TITLE_SUFFIX) ? title : title + TITLE_SUFFIX;
    const canonicalUrl = path ? BASE_URL + path : BASE_URL;
    const finalOgUrl = ogUrl || canonicalUrl;
    const finalOgImage = resolveOgImage(ogImage);

    document.title = fullTitle;

    const metaTags: Record<string, { property?: string; name?: string; content: string }> = {
      description: {
        name: "description",
        content: description,
      },
      "og:title": {
        property: "og:title",
        content: ogTitle || title,
      },
      "og:description": {
        property: "og:description",
        content: ogDescription || description,
      },
      "og:type": {
        property: "og:type",
        content: ogType,
      },
      "og:url": {
        property: "og:url",
        content: finalOgUrl,
      },
      "og:image": {
        property: "og:image",
        content: finalOgImage,
      },
      "og:site_name": {
        property: "og:site_name",
        content: BRAND.siteName,
      },
      "twitter:card": {
        name: "twitter:card",
        content: twitterCard,
      },
      "twitter:title": {
        name: "twitter:title",
        content: ogTitle || title,
      },
      "twitter:description": {
        name: "twitter:description",
        content: ogDescription || description,
      },
      "twitter:image": {
        name: "twitter:image",
        content: finalOgImage,
      },
      ...(noindex
        ? {
            robots: {
              name: "robots",
              content: "noindex, nofollow",
            },
          }
        : {}),
    };

    Object.entries(metaTags).forEach(([_key, { property, name, content }]) => {
      const selector = property ? `meta[property="${property}"]` : `meta[name="${name}"]`;
      let tag = document.querySelector(selector);

      if (!tag) {
        tag = document.createElement("meta");
        if (property) {
          tag.setAttribute("property", property);
        } else if (name) {
          tag.setAttribute("name", name);
        }
        tag.setAttribute("data-seo", "managed");
        document.head.appendChild(tag);
      }

      tag.setAttribute("content", content);
      tag.setAttribute("data-seo", "managed");
    });

    const existingCanonicals = document.querySelectorAll('link[rel="canonical"]');
    let canonicalLink: HTMLLinkElement | null = null;
    existingCanonicals.forEach((node, idx) => {
      if (idx === 0) {
        canonicalLink = node as HTMLLinkElement;
      } else {
        node.parentNode?.removeChild(node);
      }
    });
    if (!canonicalLink) {
      canonicalLink = document.createElement("link");
      canonicalLink.rel = "canonical";
      document.head.appendChild(canonicalLink);
    }
    canonicalLink.setAttribute("data-seo", "managed");
    canonicalLink.href = canonicalUrl;

    if (jsonLd) {
      let jsonLdScript = document.querySelector('script[type="application/ld+json"][data-seo="managed"]') as HTMLScriptElement | null;
      if (!jsonLdScript) {
        jsonLdScript = document.createElement("script");
        jsonLdScript.type = "application/ld+json";
        jsonLdScript.setAttribute("data-seo", "managed");
        document.head.appendChild(jsonLdScript);
      }
      jsonLdScript.textContent = JSON.stringify(jsonLd);
    }

    return () => {
      document.title = DEFAULT_FALLBACK_TITLE;

      const managedElements = document.querySelectorAll('[data-seo="managed"]');
      managedElements.forEach((el) => {
        el.remove();
      });
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.title, props.description, props.path, props.ogType, props.ogImage, props.noindex, JSON.stringify(props.jsonLd)]);
}
